/**
 * Reluxe Custom Filter Sidebar
 * Connects to our DO server API to provide filtering for 25k+ products
 * Bypasses Shopify's 5,000 product filter limit
 */

(function() {
  'use strict';

  const API_BASE = 'https://api.thereluxe.com';
  const STORE_URL = 'https://thereluxe.com';

  // State
  let currentFilters = {
    collection: 'all',
    brands: [],
    category: '',
    product_type: '',
    color: '',
    min_price: null,
    max_price: null,
    availability: 'all',
    page: 1,
    per_page: 48,
    sort: 'newest',
    search: ''  // Search query for /search pages
  };

  // Store the initial collection-based filters (from URL)
  // These are "locked" and shouldn't be cleared by clearAll
  let collectionFilters = {
    brand: null,
    category: null,
    product_type: null,  // Product type from collection slug (e.g., "Brooch")
    tag: null  // Special tag filter (e.g., "new" for new arrivals)
  };

  let filterData = null;
  let isLoading = false;

  // Optimize Cloudinary URLs for smaller file sizes
  function optimizeImageUrl(url, width = 400) {
    if (!url) return url;
    // Add width optimization to Cloudinary URLs
    if (url.includes('cloudinary.com')) {
      // Insert w_400,q_auto after the transformation params
      return url.replace('/c_pad,', `/c_pad,w_${width},q_auto,f_auto,`).replace('/c_pad%2C', `/c_pad%2Cw_${width}%2Cq_auto%2Cf_auto%2C`);
    }
    return url;
  }

  // Detect current collection from URL and extract filters
  function detectCollection() {
    const path = window.location.pathname;

    // Check if this is a search page
    if (path === '/search' || path.startsWith('/search')) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q') || '';
      return { collection: 'all', brand: null, category: null, product_type: null, search: searchQuery, isSearch: true };
    }

    // Check for main department collections
    if (path.includes('/collections/women')) return { collection: 'women', brand: null, category: null };
    if (path.includes('/collections/men')) return { collection: 'men', brand: null, category: null };

    // Check for new arrivals collection - filter by last 30 days
    if (path.includes('/collections/new-arrivals') || path.includes('/collections/new')) {
      return { collection: 'all', brand: null, category: null, product_type: null, tag: 'new' };
    }

    // For other collections, try to parse brand and category from the URL
    // Examples: /collections/chanel-brooches, /collections/louis-vuitton-bags
    const match = path.match(/\/collections\/([^\/]+)/);
    if (match) {
      const collectionSlug = match[1].toLowerCase();

      // Product type keywords - these filter by product_type column in DB
      // Maps URL slug keyword to product_type value
      const productTypeKeywords = {
        'brooches': 'Brooch',
        'brooch': 'Brooch',
        'earrings': 'Earrings',
        'earring': 'Earrings',
        'necklaces': 'Necklace',
        'necklace': 'Necklace',
        'bracelets': 'Bracelet',
        'bracelet': 'Bracelet',
        'rings': 'Ring',
        'ring': 'Ring',
        'pendants': 'Pendant',
        'pendant': 'Pendant',
        'scarves': 'Scarf',
        'scarf': 'Scarf',
        'sunglasses': 'Sunglasses',
        'hats': 'Hat',
        'hat': 'Hat',
        'wallets': 'Wallet',
        'wallet': 'Wallet',
        'watches': 'Watch',
        'watch': 'Watch'
      };

      // Category keywords - these filter by actual category field
      const categoryKeywords = {
        'bags': 'Bags',
        'bag': 'Bags',
        'wallets': 'Wallets',
        'wallet': 'Wallets',
        'watches': 'Watches',
        'watch': 'Watches',
        'scarves': 'Accessories',
        'scarf': 'Accessories',
        'belts': 'Accessories',
        'belt': 'Accessories',
        'shoes': 'Shoes',
        'sneakers': 'Shoes',
        'clothing': 'Clothing',
        'jackets': 'Clothing',
        'coats': 'Clothing'
      };

      // Known brand slugs (using title case to match DB)
      const brandMap = {
        'chanel': 'Chanel',
        'louis-vuitton': 'Louis Vuitton',
        'gucci': 'Gucci',
        'hermes': 'Hermes',
        'prada': 'Prada',
        'dior': 'Dior',
        'fendi': 'Fendi',
        'burberry': 'Burberry',
        'bottega-veneta': 'Bottega Veneta',
        'balenciaga': 'Balenciaga',
        'celine': 'Celine',
        'saint-laurent': 'Saint Laurent',
        'ysl': 'Saint Laurent',
        'cartier': 'Cartier',
        'tiffany': 'Tiffany & Co.',
        'rolex': 'Rolex',
        'omega': 'Omega',
        'bvlgari': 'Bvlgari',
        'van-cleef': 'Van Cleef & Arpels',
        'loewe': 'Loewe',
        'chloe': 'Chloe',
        'valentino': 'Valentino',
        'givenchy': 'Givenchy',
        'versace': 'Versace',
        'miu-miu': 'Miu Miu',
        'coach': 'Coach',
        'goyard': 'Goyard',
        'chrome-hearts': 'Chrome Hearts'
      };

      let detectedBrand = null;
      let detectedCategory = null;
      let detectedProductType = null;

      // Check for brand in slug
      for (const [slug, brand] of Object.entries(brandMap)) {
        if (collectionSlug.includes(slug)) {
          detectedBrand = brand;
          break;
        }
      }

      // Check for product type keywords (filters by product_type column)
      for (const [keyword, productType] of Object.entries(productTypeKeywords)) {
        if (collectionSlug.includes(keyword)) {
          detectedProductType = productType;
          break;
        }
      }

      // Check for category in slug (only if no product type found)
      if (!detectedProductType) {
        for (const [keyword, category] of Object.entries(categoryKeywords)) {
          if (collectionSlug.includes(keyword)) {
            detectedCategory = category;
            break;
          }
        }
      }

      return { collection: 'all', brand: detectedBrand, category: detectedCategory, product_type: detectedProductType, tag: null };
    }

    return { collection: 'all', brand: null, category: null, product_type: null, tag: null };
  }

  // Build filter URL params
  function buildQueryParams() {
    const params = new URLSearchParams();
    params.set('collection', currentFilters.collection);

    // Combine collection-locked brand with user-selected brands
    let allBrands = [...currentFilters.brands];
    if (collectionFilters.brand && !allBrands.includes(collectionFilters.brand)) {
      allBrands.unshift(collectionFilters.brand);
    }
    if (allBrands.length > 0) {
      params.set('brand', allBrands.join(','));
    }

    // Use collection category if set, otherwise user-selected
    const category = currentFilters.category || collectionFilters.category;
    if (category) params.set('category', category);

    // Product type - combine collection-locked with user-selected
    const productType = currentFilters.product_type || collectionFilters.product_type;
    if (productType) params.set('product_type', productType);

    if (currentFilters.color) params.set('color', currentFilters.color);
    if (currentFilters.min_price !== null) params.set('min_price', currentFilters.min_price);
    if (currentFilters.max_price !== null) params.set('max_price', currentFilters.max_price);
    if (currentFilters.availability !== 'all') params.set('availability', currentFilters.availability);
    if (currentFilters.search) params.set('search', currentFilters.search);

    // Tag filter (for new arrivals, sale, etc.)
    if (collectionFilters.tag) params.set('tag', collectionFilters.tag);

    params.set('page', currentFilters.page);
    params.set('per_page', currentFilters.per_page);
    params.set('sort', currentFilters.sort);
    return params.toString();
  }

  // Track search query for analytics
  function trackSearchQuery(query, resultCount) {
    if (!query || query.length < 2) return;

    // Get session ID
    let sessionId = sessionStorage.getItem('reluxe_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      sessionStorage.setItem('reluxe_session_id', sessionId);
    }

    // Get customer ID if available
    const customerId = window.ReluxeAuth?.getCustomerId?.() || null;

    // Send to analytics endpoint (fire and forget)
    fetch(`${API_BASE}/api/analytics/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        result_count: resultCount,
        session_id: sessionId,
        customer_id: customerId
      }),
      credentials: 'include'
    }).catch(() => {
      // Silently fail - analytics shouldn't break the page
    });
  }

  // Fetch filtered products from API
  async function fetchFilters() {
    if (isLoading) return;
    isLoading = true;

    showLoading();

    const url = `${API_BASE}/api/filters?${buildQueryParams()}`;
    console.log('[ReluxeFilters] Fetching:', url);
    console.time('[ReluxeFilters] API fetch');

    try {
      const response = await fetch(url);
      console.timeEnd('[ReluxeFilters] API fetch');
      console.log('[ReluxeFilters] Response status:', response.status);

      console.time('[ReluxeFilters] JSON parse');
      if (!response.ok) {
        const text = await response.text();
        console.error('[ReluxeFilters] API error response:', text);
        throw new Error('API error: ' + response.status);
      }

      filterData = await response.json();
      console.timeEnd('[ReluxeFilters] JSON parse');
      console.log('[ReluxeFilters] Loaded', filterData.total, 'products');

      console.time('[ReluxeFilters] Render');
      renderSidebar();
      renderProducts();
      renderPagination();
      console.timeEnd('[ReluxeFilters] Render');
      console.timeEnd('[ReluxeFilters] fetchFilters');
      console.timeEnd('[ReluxeFilters] Total init time');

      // Track search analytics (only on first page to avoid duplicates)
      if (currentFilters.search && currentFilters.page === 1) {
        trackSearchQuery(currentFilters.search, filterData.total);
      }
    } catch (error) {
      console.error('[ReluxeFilters] Fetch error:', error);
      showError('Unable to load filters. Please refresh the page. Error: ' + error.message);
    } finally {
      isLoading = false;
      hideLoading();
    }
  }

  // Count active filters
  function countActiveFilters() {
    let count = 0;
    if (currentFilters.brands.length > 0) count += currentFilters.brands.length;
    if (currentFilters.category) count++;
    if (currentFilters.product_type) count++;
    if (currentFilters.color) count++;
    if (currentFilters.min_price !== null) count++;
    if (currentFilters.max_price !== null) count++;
    if (currentFilters.availability !== 'all') count++;
    return count;
  }

  // Render mobile filter toggle button
  function renderMobileToggle() {
    // Check if button already exists
    let toggleBtn = document.querySelector('.reluxe-mobile-filter-toggle');
    const sidebar = document.getElementById('reluxe-filter-sidebar');

    if (!sidebar) return;

    if (!toggleBtn) {
      // Create the toggle button
      toggleBtn = document.createElement('button');
      toggleBtn.className = 'reluxe-mobile-filter-toggle';
      toggleBtn.onclick = function() {
        ReluxeFilters.toggleMobileFilters();
      };
      // Insert before the sidebar
      sidebar.parentNode.insertBefore(toggleBtn, sidebar);
    }

    // Update button content with filter count
    const activeCount = countActiveFilters();
    const badgeHtml = activeCount > 0 ? `<span class="reluxe-filter-count-badge">${activeCount}</span>` : '';
    const isOpen = sidebar.classList.contains('reluxe-sidebar-open');

    toggleBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
      ${isOpen ? 'Hide Filters' : 'Show Filters'}${badgeHtml}
    `;
  }

  // Render filter sidebar
  function renderSidebar() {
    const sidebar = document.getElementById('reluxe-filter-sidebar');
    if (!sidebar || !filterData) return;

    const { filters, applied_filters } = filterData;

    // Render mobile toggle button
    renderMobileToggle();

    // Show search query if on search page
    const searchHeader = currentFilters.search ? `
      <div class="reluxe-search-query">
        <span class="reluxe-search-query__label">Results for:</span>
        <span class="reluxe-search-query__term">"${currentFilters.search}"</span>
      </div>
    ` : '';

    let html = `
      ${searchHeader}
      <div class="reluxe-filter-header">
        <h3>Filter By</h3>
        <button class="reluxe-clear-all" onclick="ReluxeFilters.clearAll()">Clear All</button>
      </div>
    `;

    // Availability filter removed - only in-stock items are shown

    // Price range filter
    html += `
      <div class="reluxe-filter-group">
        <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
          Price <span class="reluxe-toggle">−</span>
        </div>
        <div class="reluxe-filter-options">
          <div class="reluxe-price-inputs">
            <input type="number" id="price-min" placeholder="Min"
              value="${applied_filters.min_price || ''}"
              onchange="ReluxeFilters.setPriceRange()">
            <span>to</span>
            <input type="number" id="price-max" placeholder="Max"
              value="${applied_filters.max_price || ''}"
              onchange="ReluxeFilters.setPriceRange()">
          </div>
          <div class="reluxe-price-range-info">
            $${Math.floor(filters.price_range.min)} - $${Math.ceil(filters.price_range.max)}
          </div>
        </div>
      </div>
    `;

    // Brand filter
    if (filters.brands && filters.brands.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Brand <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
            <input type="text" class="reluxe-filter-search"
              placeholder="Search brands..."
              oninput="ReluxeFilters.filterBrandList(this.value)">
            <div class="reluxe-brand-list">
      `;

      filters.brands.forEach(brand => {
        const isChecked = applied_filters.brands.includes(brand.name);
        html += `
          <label class="reluxe-filter-option" data-brand="${brand.name.toLowerCase()}">
            <input type="checkbox" value="${brand.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleBrand('${brand.name.replace(/'/g, "\\'")}')">
            <span>${brand.name}</span>
            <span class="reluxe-count">(${brand.count})</span>
          </label>
        `;
      });

      html += `
            </div>
          </div>
        </div>
      `;
    }

    // Category filter
    if (filters.categories && filters.categories.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Category <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
      `;

      filters.categories.forEach(cat => {
        const isSelected = applied_filters.category === cat.name;
        html += `
          <label class="reluxe-filter-option">
            <input type="radio" name="category" value="${cat.name}"
              ${isSelected ? 'checked' : ''}
              onchange="ReluxeFilters.setCategory('${cat.name.replace(/'/g, "\\'")}')">
            <span>${cat.name}</span>
            <span class="reluxe-count">(${cat.count})</span>
          </label>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    // Product Type filter (Shoulder Bag, Ring, Necklace, etc.)
    if (filters.product_types && filters.product_types.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Product Type <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
            <input type="text" class="reluxe-filter-search"
              placeholder="Search types..."
              oninput="ReluxeFilters.filterProductTypeList(this.value)">
            <div class="reluxe-product-type-list">
      `;

      filters.product_types.forEach(pt => {
        const isSelected = applied_filters.product_type === pt.name ||
                          currentFilters.product_type === pt.name;
        html += `
          <label class="reluxe-filter-option" data-product-type="${pt.name.toLowerCase()}">
            <input type="radio" name="product_type" value="${pt.name}"
              ${isSelected ? 'checked' : ''}
              onchange="ReluxeFilters.setProductType('${pt.name.replace(/'/g, "\\'")}')">
            <span>${pt.name}</span>
            <span class="reluxe-count">(${pt.count.toLocaleString()})</span>
          </label>
        `;
      });

      html += `
            </div>
          </div>
        </div>
      `;
    }

    // Color filter
    if (filters.colors && filters.colors.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Color <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
      `;

      filters.colors.forEach(color => {
        const isSelected = applied_filters.color === color.name;
        html += `
          <label class="reluxe-filter-option">
            <input type="radio" name="color" value="${color.name}"
              ${isSelected ? 'checked' : ''}
              onchange="ReluxeFilters.setColor('${color.name.replace(/'/g, "\\'")}')">
            <span>${color.name}</span>
            <span class="reluxe-count">(${color.count})</span>
          </label>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    sidebar.innerHTML = html;
  }

  // Variant ID cache (persists across page navigation within session)
  const variantCache = {};

  // Favorites functionality
  let likesCache = {}; // Cache for like counts
  let customerFavorites = []; // Customer's favorites (loaded from server)
  let favoritesLoaded = false;
  let reluxeUserId = null;  // Our custom auth user ID (from magic link)
  let authChecked = false;  // Whether we've checked our auth session

  // Check our custom auth session (magic link based)
  async function checkReluxeAuth() {
    if (authChecked) return reluxeUserId;
    authChecked = true;

    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        method: 'GET',
        credentials: 'include'  // Send cookies
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user && data.user.id) {
          reluxeUserId = `reluxe_${data.user.id}`;
          console.log('[Reluxe] Logged in via Reluxe auth:', data.user.email);
          return reluxeUserId;
        }
      }
    } catch (e) {
      console.log('[Reluxe] Auth check failed:', e);
    }

    return null;
  }

  function getCustomerId() {
    // First check our custom auth (already fetched and cached)
    if (reluxeUserId) {
      console.log('[Reluxe] Using Reluxe auth userId:', reluxeUserId);
      return reluxeUserId;
    }

    // Fallback to Shopify customer ID (for backwards compatibility)
    // Method 1: Injected by Liquid template (most reliable)
    if (window.ReluxeCustomerId) {
      console.log('[Reluxe] Found customer ID via ReluxeCustomerId:', window.ReluxeCustomerId);
      return window.ReluxeCustomerId;
    }
    // Method 2: Shopify analytics (set asynchronously, may take time)
    if (window.__st && window.__st.cid) {
      console.log('[Reluxe] Found customer ID via __st.cid:', window.__st.cid);
      return window.__st.cid;
    }
    // Method 3: Check ShopifyAnalytics
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerId) {
      console.log('[Reluxe] Found customer ID via ShopifyAnalytics:', window.ShopifyAnalytics.meta.page.customerId);
      return window.ShopifyAnalytics.meta.page.customerId;
    }
    console.log('[Reluxe] No customer ID found');
    return null;
  }

  // Wait for customer ID to become available
  function waitForCustomerId(maxWait = 3000) {
    return new Promise((resolve) => {
      const startTime = Date.now();
      function check() {
        const cid = getCustomerId();
        if (cid) {
          resolve(cid);
          return;
        }
        if (Date.now() - startTime < maxWait) {
          setTimeout(check, 100);
        } else {
          resolve(null);
        }
      }
      check();
    });
  }

  function isCustomerLoggedIn() {
    return !!getCustomerId();
  }

  function isProductFavorited(handle) {
    return customerFavorites.includes(handle);
  }

  function getLikeCount(handle) {
    return likesCache[handle] || 0;
  }

  // Load customer's favorites from server
  async function loadCustomerFavorites() {
    const customerId = getCustomerId();
    console.log('[ReluxeFilters] loadCustomerFavorites: customerId=', customerId, 'favoritesLoaded=', favoritesLoaded);

    if (!customerId) {
      console.log('[ReluxeFilters] No customer ID, skipping favorites load');
      return;
    }

    // Always reload to ensure fresh data (removed favoritesLoaded check for debugging)
    // if (favoritesLoaded) return;

    try {
      const url = `${API_BASE}/api/customer/${customerId}/favorites`;
      console.log('[ReluxeFilters] Fetching favorites from:', url);

      const response = await fetch(url);
      console.log('[ReluxeFilters] Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[ReluxeFilters] Favorites data:', data);

        customerFavorites = data.favorites || [];
        favoritesLoaded = true;

        // Update UI to show favorited items
        customerFavorites.forEach(handle => {
          const btn = document.querySelector(`[data-handle="${handle}"] .reluxe-product__favorite`);
          if (btn) {
            btn.classList.add('reluxe-product__favorite--active');
            btn.setAttribute('aria-label', 'Remove from favorites');
          }
        });

        console.log('[ReluxeFilters] Loaded', customerFavorites.length, 'favorites:', customerFavorites);
      } else {
        console.error('[ReluxeFilters] Failed to load favorites, status:', response.status);
      }
    } catch (e) {
      console.error('[ReluxeFilters] Failed to load customer favorites:', e);
    }
  }

  // Fetch like counts for a batch of handles
  async function fetchLikeCounts(handles) {
    if (!handles || handles.length === 0) return;

    try {
      const response = await fetch(`${API_BASE}/api/likes?handles=${handles.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        Object.assign(likesCache, data.likes);

        // Update displayed counts
        handles.forEach(handle => {
          const countEl = document.querySelector(`[data-handle="${handle}"] .reluxe-product__favorite-count`);
          if (countEl) {
            const count = likesCache[handle] || 0;
            countEl.textContent = count > 0 ? count : '';
          }
        });
      }
    } catch (e) {
      console.log('[ReluxeFilters] Failed to fetch likes:', e);
    }

    // Also load customer favorites if logged in
    loadCustomerFavorites();
  }

  // Toggle favorite on server
  async function toggleFavoriteOnServer(handle, isAdding) {
    const customerId = getCustomerId();
    if (!customerId) {
      console.log('[ReluxeFilters] toggleFavoriteOnServer: No customer ID');
      return { success: false };
    }

    const method = isAdding ? 'POST' : 'DELETE';
    const url = `${API_BASE}/api/customer/${customerId}/favorites/${handle}`;
    console.log(`[ReluxeFilters] ${method} ${url}`);

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      console.log('[ReluxeFilters] Server response:', data);

      if (response.ok && data.success) {
        // Update likes cache with new count
        likesCache[handle] = data.likes;
        return { success: true, likes: data.likes, action: data.action };
      } else {
        console.error('[ReluxeFilters] Server error:', data);
        return { success: false };
      }
    } catch (e) {
      console.error('[ReluxeFilters] Failed to toggle favorite:', e);
      return { success: false };
    }
  }

  function showSignInModal() {
    // Store return URL in localStorage for after sign-in
    localStorage.setItem('reluxe_return_url', window.location.pathname + window.location.search);

    // Create modal overlay with magic link email form
    const modal = document.createElement('div');
    modal.className = 'reluxe-signin-modal';
    modal.innerHTML = `
      <div class="reluxe-signin-modal__backdrop" onclick="ReluxeFilters.closeSignInModal()"></div>
      <div class="reluxe-signin-modal__content">
        <button class="reluxe-signin-modal__close" onclick="ReluxeFilters.closeSignInModal()">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
        <div class="reluxe-signin-modal__icon">
          <svg viewBox="0 0 24 24" width="48" height="48">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </div>
        <h3>Save Your Favorites</h3>
        <p>Enter your email to sign in or create an account. We'll send you a secure link.</p>
        <form class="reluxe-signin-modal__form" onsubmit="ReluxeFilters.submitMagicLink(event)">
          <input type="email"
                 class="reluxe-signin-modal__input"
                 placeholder="Enter your email"
                 required
                 autocomplete="email"
                 id="reluxe-signin-email">
          <button type="submit" class="reluxe-signin-modal__btn reluxe-signin-modal__btn--primary">
            <span class="reluxe-signin-modal__btn-text">Continue</span>
            <span class="reluxe-signin-modal__btn-loading" style="display:none;">Sending...</span>
          </button>
        </form>
        <p class="reluxe-signin-modal__hint">No password needed - we'll email you a sign-in link</p>
        <div class="reluxe-signin-modal__success" style="display:none;">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="#22c55e">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <p>Check your email! We sent you a sign-in link.</p>
        </div>
        <div class="reluxe-signin-modal__error" style="display:none;"></div>
      </div>
    `;
    document.body.appendChild(modal);
    document.body.classList.add('reluxe-modal-open');

    // Focus the email input
    setTimeout(() => {
      const input = document.getElementById('reluxe-signin-email');
      if (input) input.focus();
    }, 100);

    // Add escape key listener
    document.addEventListener('keydown', handleEscKey);
  }

  async function submitMagicLink(event) {
    event.preventDefault();

    const form = event.target;
    const emailInput = document.getElementById('reluxe-signin-email');
    const btnText = form.querySelector('.reluxe-signin-modal__btn-text');
    const btnLoading = form.querySelector('.reluxe-signin-modal__btn-loading');
    const successEl = document.querySelector('.reluxe-signin-modal__success');
    const errorEl = document.querySelector('.reluxe-signin-modal__error');
    const submitBtn = form.querySelector('button[type="submit"]');

    const email = emailInput.value.trim();
    if (!email) return;

    // Show loading state
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline';
    submitBtn.disabled = true;
    errorEl.style.display = 'none';

    try {
      const returnUrl = localStorage.getItem('reluxe_return_url') || window.location.pathname;

      const response = await fetch(`${API_BASE}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, return_url: returnUrl }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // Show success message
        form.style.display = 'none';
        document.querySelector('.reluxe-signin-modal__hint').style.display = 'none';
        successEl.style.display = 'block';
      } else {
        // Show error
        errorEl.textContent = data.error || 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
        submitBtn.disabled = false;
      }
    } catch (e) {
      console.error('[ReluxeFilters] Magic link error:', e);
      errorEl.textContent = 'Connection error. Please try again.';
      errorEl.style.display = 'block';
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
      submitBtn.disabled = false;
    }
  }

  function handleEscKey(e) {
    if (e.key === 'Escape') {
      closeSignInModal();
    }
  }

  function closeSignInModal() {
    const modal = document.querySelector('.reluxe-signin-modal');
    if (modal) {
      modal.remove();
      document.body.classList.remove('reluxe-modal-open');
      document.removeEventListener('keydown', handleEscKey);
    }
  }

  async function toggleFavorite(handle, button) {
    // Check if logged in - require sign in to like
    if (!isCustomerLoggedIn()) {
      showSignInModal();
      return;
    }

    // Check current state - use BUTTON CLASS as source of truth (more reliable than array)
    // The button class is set when favorites are loaded and when toggling
    const isCurrentlyFavorited = button.classList.contains('reluxe-product__favorite--active');
    const countEl = button.querySelector('.reluxe-product__favorite-count');
    let currentCount = likesCache[handle] || 0;

    console.log(`[ReluxeFilters] toggleFavorite: handle=${handle}, isCurrentlyFavorited=${isCurrentlyFavorited} (from button class), customerFavorites array=`, customerFavorites);

    if (isCurrentlyFavorited) {
      // Remove from favorites
      console.log('[ReluxeFilters] Removing from favorites...');

      // Update UI optimistically
      button.classList.remove('reluxe-product__favorite--active');
      button.setAttribute('aria-label', 'Add to favorites');
      currentCount = Math.max(0, currentCount - 1);
      likesCache[handle] = currentCount;
      if (countEl) countEl.textContent = currentCount > 0 ? currentCount : '';

      // Also update the array (for consistency)
      const index = customerFavorites.indexOf(handle);
      if (index > -1) customerFavorites.splice(index, 1);

      // Call server
      const result = await toggleFavoriteOnServer(handle, false);
      console.log('[ReluxeFilters] Remove result:', result);

      if (result.success) {
        // Update with actual server count
        likesCache[handle] = result.likes;
        if (countEl) countEl.textContent = result.likes > 0 ? result.likes : '';
      } else {
        // Revert on failure
        console.error('[ReluxeFilters] Remove failed, reverting UI');
        button.classList.add('reluxe-product__favorite--active');
        button.setAttribute('aria-label', 'Remove from favorites');
        customerFavorites.push(handle);
      }

      updateHeaderHeartBadge();
    } else {
      // Add to favorites
      console.log('[ReluxeFilters] Adding to favorites...');

      // Update UI optimistically
      button.classList.add('reluxe-product__favorite--active');
      button.setAttribute('aria-label', 'Remove from favorites');
      currentCount = currentCount + 1;
      likesCache[handle] = currentCount;
      if (countEl) countEl.textContent = currentCount;

      // Also update the array (for consistency)
      if (!customerFavorites.includes(handle)) {
        customerFavorites.push(handle);
      }

      // Add little animation
      button.classList.add('reluxe-product__favorite--pulse');
      setTimeout(() => {
        button.classList.remove('reluxe-product__favorite--pulse');
      }, 300);

      // Call server
      const result = await toggleFavoriteOnServer(handle, true);
      console.log('[ReluxeFilters] Add result:', result);

      if (result.success) {
        // Update with actual server count
        likesCache[handle] = result.likes;
        if (countEl) countEl.textContent = result.likes > 0 ? result.likes : '';
      } else {
        // Revert on failure
        console.error('[ReluxeFilters] Add failed, reverting UI');
        button.classList.remove('reluxe-product__favorite--active');
        button.setAttribute('aria-label', 'Add to favorites');
        const idx = customerFavorites.indexOf(handle);
        if (idx > -1) customerFavorites.splice(idx, 1);
      }

      updateHeaderHeartBadge();
    }
  }

  // Render product grid with images from our API
  async function renderProducts() {
    const grid = document.getElementById('reluxe-product-grid');
    if (!grid || !filterData) return;

    const { products, total } = filterData;

    // Update product count
    const countEl = document.getElementById('reluxe-product-count');
    if (countEl) {
      countEl.textContent = `${total.toLocaleString()} products`;
    }

    if (products.length === 0) {
      grid.innerHTML = `
        <div class="reluxe-no-results">
          <p>No products match your filters.</p>
          <button onclick="ReluxeFilters.clearAll()">Clear all filters</button>
        </div>
      `;
      return;
    }

    // Render products IMMEDIATELY without waiting for variant IDs
    let html = '<ul class="reluxe-products-grid">';

    products.forEach(product => {
      const imageUrl = product.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
      const hoverImageUrl = product.image_hover || '';
      const soldOutClass = !product.available ? 'reluxe-product--soldout' : '';
      const hasHoverImage = hoverImageUrl ? 'reluxe-product--has-hover' : '';

      // Add to cart button - will fetch variant ID on first hover/click
      const addToCartBtn = product.available ? `
        <button class="reluxe-product__add-to-cart" data-handle="${product.handle}" onclick="event.preventDefault(); ReluxeFilters.addToCartByHandle('${product.handle}', this)">
          Add to Cart
        </button>
      ` : '';

      // Check if product is in favorites
      const isFavorited = isProductFavorited(product.handle);

      html += `
        <li class="reluxe-product ${soldOutClass} ${hasHoverImage}" data-handle="${product.handle}">
          <a href="/products/${product.handle}" class="reluxe-product__link">
            <div class="reluxe-product__media">
              <img class="reluxe-product__img reluxe-product__img--primary" src="${optimizeImageUrl(imageUrl, 400)}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400" onerror="this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'">
              ${hoverImageUrl ? `<img class="reluxe-product__img reluxe-product__img--hover" src="${optimizeImageUrl(hoverImageUrl, 400)}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400">` : ''}
              ${!product.available ? '<span class="reluxe-product__badge">Sold Out</span>' : ''}
              ${addToCartBtn}
              <button class="reluxe-product__favorite ${isFavorited ? 'reluxe-product__favorite--active' : ''}"
                      data-handle="${product.handle}"
                      onclick="event.preventDefault(); event.stopPropagation(); ReluxeFilters.toggleFavorite('${product.handle}', this)"
                      aria-label="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}">
                <svg viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                <span class="reluxe-product__favorite-count">${getLikeCount(product.handle) || ''}</span>
              </button>
            </div>
            <div class="reluxe-product__details">
              <span class="reluxe-product__vendor">${product.brand || ''}</span>
              <h3 class="reluxe-product__title">${product.title}</h3>
              <span class="reluxe-product__price">$${product.price ? product.price.toFixed(2) : '0.00'}</span>
            </div>
          </a>
        </li>
      `;
    });

    html += '</ul>';
    grid.innerHTML = html;

    // Pre-fetch variant IDs in background (non-blocking) for faster add-to-cart
    const availableHandles = products.filter(p => p.available).map(p => p.handle);
    prefetchVariantIds(availableHandles);

    // Fetch like counts for all products
    const allHandles = products.map(p => p.handle);
    fetchLikeCounts(allHandles);
  }

  // Pre-fetch variant IDs in background (doesn't block rendering)
  function prefetchVariantIds(handles) {
    // Only fetch handles not already cached
    const uncached = handles.filter(h => !variantCache[h]);
    if (uncached.length === 0) return;

    // Fetch all in parallel (Shopify can handle it)
    uncached.forEach(handle => {
      fetch(`/products/${handle}.js`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            const variant = data.variants.find(v => v.available) || data.variants[0];
            if (variant) {
              variantCache[handle] = variant.id;
            }
          }
        })
        .catch(() => {}); // Ignore errors
    });
  }

  // Get variant ID (from cache or fetch)
  async function getVariantId(handle) {
    if (variantCache[handle]) {
      return variantCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const data = await response.json();
        const variant = data.variants.find(v => v.available) || data.variants[0];
        if (variant) {
          variantCache[handle] = variant.id;
          return variant.id;
        }
      }
    } catch (e) {}
    return null;
  }

  // Add to cart by handle (fetches variant ID if not cached)
  async function addToCartByHandle(handle, button) {
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      // Get variant ID (from cache or fetch)
      const variantId = await getVariantId(handle);
      if (!variantId) {
        throw new Error('Could not get variant');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        button.textContent = 'Added!';
        button.classList.add('reluxe-product__add-to-cart--added');

        // Update cart count and refresh drawer
        await updateCartCount();

        // Try to open the cart drawer
        openCartDrawer();

        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('reluxe-product__add-to-cart--added');
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error('Failed to add');
      }
    } catch (e) {
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  // Add to cart function (legacy - with variant ID directly)
  async function addToCart(variantId, button) {
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        button.textContent = 'Added!';
        button.classList.add('reluxe-product__add-to-cart--added');

        // Update cart count and refresh drawer
        await updateCartCount();

        // Try to open the cart drawer
        openCartDrawer();

        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('reluxe-product__add-to-cart--added');
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error('Failed to add');
      }
    } catch (e) {
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  // Update cart count and refresh cart drawer
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Try common cart count selectors
      const selectors = ['.cart-count', '.cart-count-bubble', '[data-cart-count]', '.cart__count', '.header__cart-count', '.cart-count-bubble span', '[data-cart-item-count]'];
      selectors.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) {
          el.textContent = cart.item_count;
        }
      });

      // Dispatch custom event that many themes listen for
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));

      // Try to refresh cart drawer by fetching and replacing its content
      await refreshCartDrawer();

    } catch (e) {
      console.log('[ReluxeFilters] Cart update error:', e);
    }
  }

  // Refresh the cart drawer content
  async function refreshCartDrawer() {
    try {
      // Fetch the cart drawer HTML from a cart page section
      const response = await fetch('/?sections=cart-drawer,cart-notification,cart-items');

      if (response.ok) {
        const sections = await response.json();

        // Update cart drawer if it exists
        Object.keys(sections).forEach(sectionId => {
          const html = sections[sectionId];

          // Try multiple common cart drawer selectors
          const drawerSelectors = [
            '#cart-drawer',
            '.cart-drawer',
            '[data-cart-drawer]',
            'cart-drawer',
            '#CartDrawer',
            '.drawer__inner'
          ];

          drawerSelectors.forEach(selector => {
            const drawer = document.querySelector(selector);
            if (drawer && html) {
              // Parse the new HTML and extract the cart content
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newContent = doc.querySelector(selector);
              if (newContent) {
                drawer.innerHTML = newContent.innerHTML;
              }
            }
          });
        });
      }

      // Also try triggering theme-specific cart open/refresh methods
      if (window.theme && typeof window.theme.cartDrawer === 'object') {
        window.theme.cartDrawer.refresh && window.theme.cartDrawer.refresh();
      }

      // For themes using Alpine.js or similar
      if (window.Alpine) {
        window.dispatchEvent(new CustomEvent('cart-updated'));
      }

      // Trigger a click on cart icon to open drawer (optional - comment out if not wanted)
      // const cartIcon = document.querySelector('[data-cart-toggle], .cart-icon, .header__icon--cart');
      // if (cartIcon) cartIcon.click();

    } catch (e) {
      // Fallback: just update the bubble count
      console.log('[ReluxeFilters] Drawer refresh fallback');
    }
  }

  // Open cart drawer
  function openCartDrawer() {
    // Method 1: Click the cart icon (most reliable)
    const cartTriggers = [
      '[data-cart-toggle]',
      '.cart-icon',
      '.header__icon--cart',
      'a[href="/cart"]',
      '.cart-link',
      '#cart-icon-bubble',
      '.js-drawer-open-cart',
      '[data-action="open-drawer"][data-drawer="cart"]'
    ];

    for (const selector of cartTriggers) {
      const trigger = document.querySelector(selector);
      if (trigger) {
        trigger.click();
        // Add click-outside-to-close functionality
        setupClickOutsideToClose();
        return;
      }
    }

    // Method 2: Open drawer directly by adding class
    const drawerSelectors = [
      { drawer: '#cart-drawer', openClass: 'is-open' },
      { drawer: '.cart-drawer', openClass: 'is-open' },
      { drawer: 'cart-drawer', openClass: 'open' },
      { drawer: '#CartDrawer', openClass: 'drawer--is-open' },
      { drawer: '.drawer', openClass: 'is-active' }
    ];

    for (const { drawer, openClass } of drawerSelectors) {
      const el = document.querySelector(drawer);
      if (el) {
        el.classList.add(openClass);
        // Also add overlay if exists
        const overlay = document.querySelector('.drawer-overlay, .js-drawer-overlay');
        if (overlay) overlay.classList.add('is-visible');
        document.body.classList.add('drawer-open', 'overflow-hidden');
        // Add click-outside-to-close functionality
        setupClickOutsideToClose();
        return;
      }
    }

    // Method 3: Dispatch cart open event
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));
    // Add click-outside-to-close functionality
    setupClickOutsideToClose();
  }

  // Setup click-outside-to-close for cart drawer
  function setupClickOutsideToClose() {
    // Wait for drawer to fully open
    setTimeout(() => {
      // Find the drawer by looking for fixed/absolute positioned elements on the right side
      const findVisibleDrawer = () => {
        // Check common drawer selectors
        const selectors = [
          'cart-drawer',
          '#cart-drawer',
          '.cart-drawer',
          '#CartDrawer',
          '[data-cart-drawer]',
          '.drawer[open]',
          '.drawer.active',
          '.drawer.is-open',
          '.side-cart',
          '.mini-cart',
          '.cart-sidebar'
        ];

        for (const sel of selectors) {
          const el = document.querySelector(sel);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 100) {
              return el;
            }
          }
        }

        // Fallback: find any large fixed element on the right
        const allElements = document.querySelectorAll('*');
        for (const el of allElements) {
          const style = window.getComputedStyle(el);
          const rect = el.getBoundingClientRect();
          if ((style.position === 'fixed' || style.position === 'absolute') &&
              rect.width > 200 && rect.width < 600 &&
              rect.height > 300 &&
              rect.right >= window.innerWidth - 10) {
            return el;
          }
        }
        return null;
      };

      const drawerEl = findVisibleDrawer();

      if (drawerEl) {
        console.log('[ReluxeFilters] Found drawer:', drawerEl.tagName, drawerEl.id, drawerEl.className);
      } else {
        console.log('[ReluxeFilters] No drawer found, using body click detection');
      }

      // Click handler
      const clickHandler = function(e) {
        // Ignore clicks on add to cart buttons
        if (e.target.closest('.reluxe-product__add-to-cart')) return;

        // Ignore clicks on cart icon
        if (e.target.closest('[href="/cart"], .cart-icon, #cart-icon-bubble, [data-cart-toggle], .header__icon--cart')) return;

        // Check if drawer still exists and is visible
        const currentDrawer = findVisibleDrawer();
        if (!currentDrawer) {
          // Drawer closed, remove listener
          document.removeEventListener('click', clickHandler, true);
          document.removeEventListener('keydown', escHandler);
          return;
        }

        // Check if click was inside the drawer
        if (currentDrawer.contains(e.target)) {
          return;
        }

        // Click was outside - close the drawer
        console.log('[ReluxeFilters] Closing drawer from outside click');
        closeCartDrawer();
        document.removeEventListener('click', clickHandler, true);
        document.removeEventListener('keydown', escHandler);
      };

      // Escape key handler
      const escHandler = function(e) {
        if (e.key === 'Escape') {
          closeCartDrawer();
          document.removeEventListener('click', clickHandler, true);
          document.removeEventListener('keydown', escHandler);
        }
      };

      // Add listeners
      setTimeout(() => {
        document.addEventListener('click', clickHandler, true);
        document.addEventListener('keydown', escHandler);
      }, 200);

    }, 500);
  }

  // Close cart drawer
  function closeCartDrawer() {
    // Method 1: Click close button
    const closeButtons = [
      '[data-drawer-close]',
      '.drawer__close',
      '.cart-drawer__close',
      '.js-drawer-close',
      '#CartDrawer .drawer__close',
      '[aria-label="Close"]'
    ];

    for (const selector of closeButtons) {
      const btn = document.querySelector(selector);
      if (btn) {
        btn.click();
        return;
      }
    }

    // Method 2: Remove open classes
    const drawerSelectors = [
      { drawer: '#cart-drawer', openClass: 'is-open' },
      { drawer: '.cart-drawer', openClass: 'is-open' },
      { drawer: 'cart-drawer', openClass: 'open' },
      { drawer: '#CartDrawer', openClass: 'drawer--is-open' },
      { drawer: '.drawer', openClass: 'is-active' }
    ];

    for (const { drawer, openClass } of drawerSelectors) {
      const el = document.querySelector(drawer);
      if (el) {
        el.classList.remove(openClass);
      }
    }

    // Remove body classes
    document.body.classList.remove('drawer-open', 'overflow-hidden', 'js-drawer-open');

    // Hide theme overlay if exists
    const overlays = document.querySelectorAll('.drawer-overlay, .js-drawer-overlay, .modal-overlay');
    overlays.forEach(o => o.classList.remove('is-visible', 'is-active'));

    // Dispatch close events
    document.dispatchEvent(new CustomEvent('cart:close'));
    document.dispatchEvent(new CustomEvent('theme:cart:close'));
  }

  // Render pagination
  function renderPagination() {
    const pagination = document.getElementById('reluxe-pagination');
    if (!pagination || !filterData) return;

    const { page, total_pages, total } = filterData;

    if (total_pages <= 1) {
      pagination.innerHTML = '';
      return;
    }

    let html = '<div class="reluxe-pagination">';

    // Previous button
    if (page > 1) {
      html += `<button onclick="ReluxeFilters.goToPage(${page - 1})">← Previous</button>`;
    }

    // Page numbers
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(total_pages, page + 2);

    if (startPage > 1) {
      html += `<button onclick="ReluxeFilters.goToPage(1)">1</button>`;
      if (startPage > 2) html += '<span class="reluxe-ellipsis">...</span>';
    }

    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === page ? 'active' : '';
      html += `<button class="${activeClass}" onclick="ReluxeFilters.goToPage(${i})">${i}</button>`;
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1) html += '<span class="reluxe-ellipsis">...</span>';
      html += `<button onclick="ReluxeFilters.goToPage(${total_pages})">${total_pages}</button>`;
    }

    // Next button
    if (page < total_pages) {
      html += `<button onclick="ReluxeFilters.goToPage(${page + 1})">Next →</button>`;
    }

    html += '</div>';
    pagination.innerHTML = html;
  }

  // UI helpers
  function showLoading() {
    const grid = document.getElementById('reluxe-product-grid');
    if (grid) {
      grid.classList.add('reluxe-loading');
    }
  }

  function hideLoading() {
    const grid = document.getElementById('reluxe-product-grid');
    if (grid) {
      grid.classList.remove('reluxe-loading');
    }
  }

  function showError(message) {
    const grid = document.getElementById('reluxe-product-grid');
    if (grid) {
      grid.innerHTML = `<div class="reluxe-error">${message}</div>`;
    }
  }

  // Public API
  window.ReluxeFilters = {
    init: async function() {
      console.time('[ReluxeFilters] fetchFilters');

      // Auth check happens in background via initFilter(), don't block here

      const detected = detectCollection();
      currentFilters.collection = detected.collection;

      // Store collection-based filters (locked, not cleared by clearAll)
      collectionFilters.brand = detected.brand;
      collectionFilters.category = detected.category;
      collectionFilters.product_type = detected.product_type;
      collectionFilters.tag = detected.tag;

      // Handle search query (for /search pages)
      if (detected.search) {
        currentFilters.search = detected.search;
      }

      console.log('[ReluxeFilters] Collection detected:', detected);
      console.log('[ReluxeFilters] Collection filters:', collectionFilters);
      if (detected.isSearch) {
        console.log('[ReluxeFilters] Search mode, query:', detected.search);
      }

      fetchFilters();
    },

    toggleGroup: function(el) {
      const options = el.nextElementSibling;
      const toggle = el.querySelector('.reluxe-toggle');
      if (options.style.display === 'none') {
        options.style.display = 'block';
        toggle.textContent = '−';
      } else {
        options.style.display = 'none';
        toggle.textContent = '+';
      }
    },

    toggleBrand: function(brand) {
      const index = currentFilters.brands.indexOf(brand);
      if (index > -1) {
        currentFilters.brands.splice(index, 1);
      } else {
        currentFilters.brands.push(brand);
      }
      currentFilters.page = 1;
      fetchFilters();
    },

    setCategory: function(category) {
      currentFilters.category = currentFilters.category === category ? '' : category;
      currentFilters.page = 1;
      fetchFilters();
    },

    setProductType: function(productType) {
      currentFilters.product_type = currentFilters.product_type === productType ? '' : productType;
      currentFilters.page = 1;
      fetchFilters();
    },

    setColor: function(color) {
      currentFilters.color = currentFilters.color === color ? '' : color;
      currentFilters.page = 1;
      fetchFilters();
    },

    setAvailability: function(value) {
      currentFilters.availability = value;
      currentFilters.page = 1;
      fetchFilters();
    },

    setPriceRange: function() {
      const min = document.getElementById('price-min').value;
      const max = document.getElementById('price-max').value;
      currentFilters.min_price = min ? parseFloat(min) : null;
      currentFilters.max_price = max ? parseFloat(max) : null;
      currentFilters.page = 1;
      fetchFilters();
    },

    setSort: function(sort) {
      currentFilters.sort = sort;
      currentFilters.page = 1;
      fetchFilters();
    },

    goToPage: function(page) {
      currentFilters.page = page;
      fetchFilters();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    clearAll: function() {
      currentFilters.brands = [];
      currentFilters.category = '';
      currentFilters.product_type = '';
      currentFilters.color = '';
      currentFilters.min_price = null;
      currentFilters.max_price = null;
      currentFilters.availability = 'all';
      currentFilters.page = 1;
      fetchFilters();
    },

    filterBrandList: function(query) {
      const list = document.querySelector('.reluxe-brand-list');
      if (!list) return;

      const options = list.querySelectorAll('.reluxe-filter-option');
      const q = query.toLowerCase();

      options.forEach(opt => {
        const brand = opt.getAttribute('data-brand');
        opt.style.display = brand.includes(q) ? 'flex' : 'none';
      });
    },

    filterProductTypeList: function(query) {
      const list = document.querySelector('.reluxe-product-type-list');
      if (!list) return;

      const options = list.querySelectorAll('.reluxe-filter-option');
      const q = query.toLowerCase();

      options.forEach(opt => {
        const productType = opt.getAttribute('data-product-type');
        opt.style.display = productType.includes(q) ? 'flex' : 'none';
      });
    },

    toggleMobileFilters: function() {
      const sidebar = document.getElementById('reluxe-filter-sidebar');
      if (!sidebar) return;

      sidebar.classList.toggle('reluxe-sidebar-open');
      renderMobileToggle(); // Update button text
    },

    addToCart: addToCart,
    addToCartByHandle: addToCartByHandle,
    toggleFavorite: toggleFavorite,
    closeSignInModal: closeSignInModal,
    submitMagicLink: submitMagicLink,
    getCustomerFavorites: function() { return customerFavorites; }
  };

  // Update header heart badge (calls global auth if available)
  function updateHeaderHeartBadge() {
    if (window.ReluxeAuth && window.ReluxeAuth.updateHeaderHeartBadge) {
      window.ReluxeAuth.updateHeaderHeartBadge();
    }
  }

  // Auto-initialize filter on collection and search pages
  async function initFilter() {
    console.time('[ReluxeFilters] Total init time');

    // Start auth check in background - DON'T await it (non-blocking)
    checkReluxeAuth().then(() => {
      console.log('[ReluxeFilters] Auth check completed in background');
    });

    const path = window.location.pathname;

    // Init filters on collection pages AND search pages
    if (path.includes('/collections/') || path === '/search' || path.startsWith('/search')) {
      ReluxeFilters.init();
    }
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilter);
  } else {
    initFilter();
  }

})();
